import { Injectable, inject } from "@angular/core";
import { EditionServiceServiceProxy, UserServiceProxy } from "@api/core";
import {
    BuyAdditionalFeatureForUsersRequest,
    BuyAdditionalFeatureRequest,
    IBuyAdditionalFeatureForUsersRequest,
} from "@api/core.models";
import {
    AvailableAdditionalItemSection,
    CustomerGatewayInfoRequest,
    CustomersApi,
    SubscriptionApi,
} from "./api/proxies";
import { Observable, shareReplay, tap } from "rxjs";
import { FeatureUsageEventsService } from "./feature-checkout/feature-usage-events.service";

export enum PaymentGateway {
    Superlogica,
    Vindi,
}

@Injectable()
export class EditionService {
    private _api = inject(EditionServiceServiceProxy);
    private _userApi = inject(UserServiceProxy);
    private _subscriptionApi = inject(SubscriptionApi);
    private _customerApi = inject(CustomersApi);
    private _usageEvents = inject(FeatureUsageEventsService);

    private readonly _availableItems$ = this._subscriptionApi.availableAdditionalItems(true).pipe(shareReplay(1));

    getGatewayInfo(tenantId: number) {
        return this._customerApi.gatewayInfoGet(tenantId);
    }

    updateGatewayInfo(tenantId: number, customerExternalId: string, subscriptionExternalId: string, gatewayId: number) {
        const request = CustomerGatewayInfoRequest.fromJS({
            customerExternalId: customerExternalId,
            subscriptionExternalId: subscriptionExternalId,
            paymentGateway: gatewayId,
        });

        return this._customerApi.gatewayInfoPatch(tenantId, request);
    }

    getTenantFeaturesUsage(featureName?: string) {
        return this._api.getTenantFeaturesUsage(featureName);
    }

    getAvailableAdditionalItems(): Observable<AvailableAdditionalItemSection[]> {
        return this._availableItems$;
    }

    acquireFeature(params: BuyAdditionalFeatureRequest) {
        const request = BuyAdditionalFeatureRequest.fromJS(params);
        return this._api.buyAdditionalFeature(request);
    }

    acquireFeatureForUsers(params: IBuyAdditionalFeatureForUsersRequest): Observable<boolean> {
        return this._userApi.buyAdditionalFeatureForUsers(BuyAdditionalFeatureForUsersRequest.fromJS(params)).pipe(
            tap((success) => {
                if (!success) {
                    return;
                }
                this._reflectAcquiredFeatureForCurrentUser(params);
                if (params.featureName) {
                    this._usageEvents.notifyAcquired(params.featureName);
                }
            })
        );
    }

    private _reflectAcquiredFeatureForCurrentUser(params: IBuyAdditionalFeatureForUsersRequest): void {
        const currentUserId = (abp as { session?: { userId?: number } }).session?.userId;
        if (currentUserId == null || !params.featureName || !params.users?.includes(Number(currentUserId))) {
            return;
        }
        const allFeatures = (abp as { features?: { allFeatures?: Record<string, { value: string }> } }).features
            ?.allFeatures;
        if (allFeatures) {
            allFeatures[params.featureName] = { value: String(params.packQuantity ?? 1) };
        }
    }
}
